import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Disclose,
  EmptyState,
  Loading,
  Permission,
  Refusal,
  hostConfig,
  kernel,
} from "@lopie/kernel-ui";
import TakeoverPanel from "./TakeoverPanel.jsx";
import Composer from "./Composer.jsx";
import { toE164, formatPhone } from "./phone.js";
import { readAll } from "./readAll.js";

/**
 * SMS Inbox \u2014 a two-pane SMS client.
 *
 * Contacts are the kernel's Person records that carry a phone number, read
 * page by page until the kernel hands back no continuation. Which people and
 * messages are visible at all is decided by this app's manifest (Person and
 * Message tagged customer, employee, vendor) intersected with the signed-in
 * person's own tags. Conversations are the kernel's Message records, which
 * the org's Twilio number's traffic is polled into hourly. Sending goes
 * through the kernel's message.send action with this app's own token.
 */

const PAGE = 50;
const MESSAGE_PAGES = 6;
const PERSON = "Person";
const MESSAGE = "Message";

function counterparty(m) {
  const f = m.fields || {};
  return f.direction === "inbound" ? f.from_number : f.to_number;
}

function tail(n) {
  const d = String(n || "").replace(/\D/g, "");
  return d.length >= 10 ? d.slice(-10) : null;
}

function phonesOf(p) {
  const v = p.fields?.phones;
  const arr = Array.isArray(v) ? v : v ? [v] : [];
  const out = arr
    .map((x) => (typeof x === "string" ? x : x?.number || x?.value || x?.e164 || ""))
    .map((x) => toE164(x) || String(x).trim())
    .filter(Boolean);
  return [...new Set(out)];
}

function emailsOf(p) {
  const v = p.fields?.emails;
  const arr = Array.isArray(v) ? v : v ? [v] : [];
  return arr.map((e) => String(e).trim().toLowerCase()).filter(Boolean);
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

export default function App({ token, claims, onSignOut }) {
  const [state, setState] = useState(null);
  const [failure, setFailure] = useState(null);
  const [selected, setSelected] = useState(null);
  const [number, setNumber] = useState(null);
  const [search, setSearch] = useState("");
  const [queued, setQueued] = useState([]);
  const [adhoc, setAdhoc] = useState([]);
  const [composing, setComposing] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const [newError, setNewError] = useState(null);

  const get = useMemo(() => kernel(token), [token]);
  const conf = hostConfig();
  const ownApi = conf?.appSmsInboxApi || "";

  const own = useCallback(
    async (path, init) => {
      const r = await fetch(ownApi + path, {
        ...init,
        headers: {
          Authorization: `Bearer ${token}`,
          "content-type": "application/json",
          ...(init?.headers || {}),
        },
      });
      const text = await r.text();
      let body = null;
      try {
        body = JSON.parse(text);
      } catch {
        body = null;
      }
      if (!r.ok) throw Object.assign(new Error("refused"), { status: r.status, body });
      return body;
    },
    [ownApi, token],
  );

  const load = useCallback(async () => {
    try {
      const schema = await get("/schema");
      const described = schema.types || [];
      const byType = Object.fromEntries(described.map((t) => [t.type, t]));

      const read = async (type, maxPages) => {
        const t = byType[type];
        if (!t?.collection) {
          return { body: null, refusal: null, missing: "The kernel's schema does not describe " + type + "." };
        }
        try {
          const body = await readAll(get, `/${t.collection}`, { limit: PAGE, maxPages });
          return { body, refusal: null };
        } catch (e) {
          return { body: null, refusal: e };
        }
      };

      const [people, messages] = await Promise.all([read(PERSON, 400), read(MESSAGE, MESSAGE_PAGES)]);
      setState({
        people,
        messages,
        personLabel: byType[PERSON]?.label || "",
        tags: (people.body || messages.body)?.permitted_tags || [],
        declaredTags: (people.body || messages.body)?.declared_tags || null,
      });
      setFailure(null);
    } catch (e) {
      setFailure(e);
      setState(null);
    }
  }, [get]);

  useEffect(() => {
    load();
  }, [load]);

  const myEmail = String(claims?.email || "").trim().toLowerCase();

  const model = useMemo(() => {
    if (!state) return null;
    const people = state.people.body?.items || [];
    const messages = state.messages.body?.items || [];
    const byNumber = {};
    const byTail = {};
    const contacts = [];
    let withoutPhone = 0;
    let meWithoutPhone = false;
    let meFound = false;
    for (const p of people) {
      const isMe = Boolean(myEmail) && emailsOf(p).includes(myEmail);
      if (isMe) meFound = true;
      const phones = phonesOf(p);
      if (!phones.length) {
        withoutPhone += 1;
        if (isMe) meWithoutPhone = true;
        continue;
      }
      const f = p.fields || {};
      const named = (state.personLabel && f[state.personLabel])
        || [f.given_name, f.family_name].filter(Boolean).join(" ");
      const c = {
        key: `person:${p.id}`,
        name: named || formatPhone(phones[0]),
        numbers: phones,
        unknown: false,
        you: isMe,
      };
      contacts.push(c);
      for (const n of phones) {
        if (!byNumber[n]) byNumber[n] = c;
        const t = tail(n);
        if (t && !byTail[t]) byTail[t] = c;
      }
    }
    const addNumber = (n) => {
      let c = byNumber[n] || byTail[tail(n)];
      if (!c) {
        c = { key: `number:${n}`, name: formatPhone(n), numbers: [n], unknown: true, you: false };
        contacts.push(c);
      }
      byNumber[n] = c;
      return c;
    };
    const threads = {};
    for (const m of messages) {
      const n = counterparty(m);
      if (!n) continue;
      const c = addNumber(n);
      (threads[c.key] ||= []).push(m);
    }
    for (const n of adhoc) addNumber(n);
    for (const k of Object.keys(threads)) {
      threads[k].sort((a, b) => String(a.fields?.sent_at).localeCompare(String(b.fields?.sent_at)));
    }
    const last = (c) => {
      const t = threads[c.key];
      return t && t.length ? t[t.length - 1] : null;
    };
    contacts.sort((a, b) => {
      if (a.you !== b.you) return a.you ? -1 : 1;
      const la = last(a)?.fields?.sent_at || "";
      const lb = last(b)?.fields?.sent_at || "";
      if (la !== lb) return lb.localeCompare(la);
      return String(a.name).localeCompare(String(b.name));
    });
    const awaiting = contacts.filter((c) => last(c)?.fields?.direction === "inbound").length;
    return {
      contacts, threads, last, byNumber, byTail, withoutPhone, awaiting,
      messageCount: messages.length, peopleCount: people.length, meFound, meWithoutPhone,
    };
  }, [state, adhoc, myEmail]);

  const startNew = () => {
    const e = toE164(newNumber);
    if (!e) {
      setNewError("Not a phone number the kernel can text. Include the country code, e.g. +44 20 7946 0958; a 10-digit number is read as +1.");
      return;
    }
    setNewError(null);
    const existing = model.byNumber[e] || model.byTail[tail(e)];
    if (!existing) setAdhoc((a) => (a.includes(e) ? a : [...a, e]));
    setSelected(existing?.key || `number:${e}`);
    setNumber(e);
    setSearch("");
    setNewNumber("");
    setComposing(false);
  };

  if (failure) {
    return (
      <Box sx={{ p: 3 }}>
        <Refusal failure={failure} actor="SMS Inbox" onRetry={load} />
      </Box>
    );
  }
  if (!state || !model) return <Loading label="Reading contacts and messages" minHeight="60vh" />;

  const q = search.trim().toLowerCase();
  const qDigits = q.replace(/\D/g, "");
  const visible = model.contacts.filter(
    (c) => !q
      || String(c.name).toLowerCase().includes(q)
      || c.numbers.some((n) => n.includes(q) || (qDigits.length >= 3 && n.replace(/\D/g, "").includes(qDigits))),
  );
  const contact = model.contacts.find((c) => c.key === selected) || null;
  const thread = contact ? model.threads[contact.key] || [] : [];
  const lastNumber = thread.length ? counterparty(thread[thread.length - 1]) : null;
  const target = contact ? (number && contact.numbers.includes(number) ? number : lastNumber || contact.numbers[0]) : null;
  const pending = contact ? queued.filter((m) => contact.numbers.includes(m.to)) : [];
  const peopleBody = state.people.body;
  const peopleIncomplete = peopleBody && peopleBody.complete === false;
  const tagList = (state.tags || []).join(", ");

  return (
    <Stack spacing={2.5} sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap" }}>
        <Stack spacing={0.5}>
          <Typography variant="h1">SMS Inbox</Typography>
          <Typography variant="body2" color="text.secondary">
            {model.awaiting} {model.awaiting === 1 ? "conversation is" : "conversations are"} awaiting a reply
            {" \u00b7 in the latest "}{model.messageCount} messages read
            {" \u00b7 "}{model.peopleCount} people read
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
          <Button size="small" onClick={load}>Refresh</Button>
          <Typography variant="body2" color="text.secondary">{claims?.username || ""}</Typography>
          <Typography
            component="button"
            variant="body2"
            onClick={onSignOut}
            sx={{ background: "none", border: 0, cursor: "pointer", color: "primary.main" }}
          >
            Sign out
          </Typography>
        </Stack>
      </Stack>

      <Permission
        gate={
          "Governed by this app's reviewed manifest \u2014 the contacts and messages below are "
          + "that ceiling intersected with your own groups."
        }
        groups={claims?.["cognito:groups"] || []}
        effective={{ permitted: state.tags, declared: state.declaredTags }}
      />

      <TakeoverPanel own={own} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, gap: 2, minWidth: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="overline" sx={{ display: "block" }}>Contacts</Typography>
            <Button size="small" variant={composing ? "outlined" : "contained"} onClick={() => { setComposing((v) => !v); setNewError(null); }}>
              {composing ? "Cancel" : "New text"}
            </Button>
          </Stack>
          {composing ? (
            <Stack spacing={1} sx={{ my: 1 }}>
              <TextField
                size="small"
                fullWidth
                autoFocus
                type="tel"
                label="To any phone number"
                placeholder="+15551234567"
                value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") startNew(); }}
                error={Boolean(newError)}
                helperText={newError || "Anyone, contact or not. A 10-digit number is read as +1."}
              />
              <Box>
                <Button size="small" variant="contained" disabled={!newNumber.trim()} onClick={startNew}>
                  Start conversation
                </Button>
              </Box>
            </Stack>
          ) : null}
          <TextField
            size="small"
            fullWidth
            placeholder="Search name or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ my: 1 }}
          />
          {state.people.refusal ? (
            <Refusal failure={state.people.refusal} actor="SMS Inbox" onRetry={load} />
          ) : null}
          {state.people.missing ? <Alert severity="warning" variant="outlined">{state.people.missing}</Alert> : null}
          {peopleIncomplete ? (
            <Alert severity="warning" variant="outlined" sx={{ mb: 1 }}>
              {peopleBody.error
                ? `A later page of people was refused after ${peopleBody.pages} pages (${model.peopleCount} read), so this list is partial.`
                : `Stopped after ${peopleBody.pages} pages of people (${model.peopleCount} read).`}
              {" "}Someone not read is shown as a number, not a name.
            </Alert>
          ) : null}
          {myEmail && model.meWithoutPhone ? (
            <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
              Your own Person record ({myEmail}) was read, but it carries no phone number the kernel
              stored, so you are not listed. The kernel stores only numbers it could normalise to E.164.
            </Alert>
          ) : null}
          {visible.length ? (
            <List dense sx={{ maxHeight: 520, overflowY: "auto" }}>
              {visible.map((c) => {
                const l = model.last(c);
                const waiting = l?.fields?.direction === "inbound";
                return (
                  <ListItemButton
                    key={c.key}
                    selected={c.key === selected}
                    onClick={() => { setSelected(c.key); setNumber(null); }}
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <span>{c.name}</span>
                          {c.you ? <Chip size="small" variant="outlined" label="you" /> : null}
                          {waiting ? <Chip size="small" variant="outlined" label="awaiting reply" /> : null}
                          {c.unknown ? <Chip size="small" variant="outlined" label="not a contact" /> : null}
                        </Stack>
                      }
                      secondary={l ? String(l.fields?.body || "").slice(0, 60) : c.numbers.map(formatPhone).join(", ")}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          ) : state.people.refusal ? null : (
            <EmptyState title={q ? "No contact matches" : "No one you may see has a phone number"} tagged={tagList}>
              <Typography variant="body2">Use New text to write to any number.</Typography>
            </EmptyState>
          )}
          {model.withoutPhone ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {model.withoutPhone} of the {model.peopleCount} people read have no phone number on record, so are not listed.
            </Typography>
          ) : null}
          {myEmail && !model.meFound && !state.people.refusal ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              No Person you may see carries your email ({myEmail}). You may see people tagged
              {tagList ? ` ${tagList}` : " with no tag at all"}; if your record exists it may carry a
              tag outside that, or be listed under another address.
            </Typography>
          ) : null}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, minWidth: 0 }}>
          {state.messages.refusal ? (
            <Refusal failure={state.messages.refusal} actor="SMS Inbox" onRetry={load} />
          ) : null}
          {!contact ? (
            <Typography variant="body2" color="text.secondary">
              Choose a contact to read the conversation and reply, or use New text to write to any number.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
                <Typography variant="h2">{contact.name}</Typography>
                {contact.numbers.map((n) => (
                  <Chip
                    key={n}
                    size="small"
                    label={n === target ? `${formatPhone(n)} \u00b7 texting` : formatPhone(n)}
                    variant={n === target ? "filled" : "outlined"}
                    onClick={() => setNumber(n)}
                  />
                ))}
                {contact.you ? <Chip size="small" variant="outlined" label="you" /> : null}
                {contact.unknown ? <Chip size="small" variant="outlined" label="not a contact" /> : null}
              </Stack>
              <Stack spacing={1} sx={{ maxHeight: 480, overflowY: "auto", pr: 1 }}>
                {thread.length === 0 && pending.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No messages with this number in the latest {model.messageCount} read. Write the first one below.
                  </Typography>
                ) : null}
                {thread.map((m) => {
                  const out = m.fields?.direction === "outbound";
                  return (
                    <Box
                      key={m.id}
                      sx={{
                        alignSelf: out ? "flex-end" : "flex-start",
                        maxWidth: "75%",
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        bgcolor: out ? "action.hover" : "background.paper",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {out ? "Sent" : "Received"} · {when(m.fields?.sent_at)}
                        {m.fields?.status ? ` \u00b7 ${m.fields.status}` : ""}
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {m.fields?.body}
                      </Typography>
                    </Box>
                  );
                })}
                {pending.map((m) => (
                  <Box
                    key={m.key}
                    sx={{
                      alignSelf: "flex-end",
                      maxWidth: "75%",
                      border: 1,
                      borderStyle: "dashed",
                      borderColor: "divider",
                      borderRadius: 2,
                      px: 1.5,
                      py: 1,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                      Queued, not yet confirmed sent · {when(m.at)}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {m.body}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              <Composer
                token={token}
                to={target}
                fresh={thread.length === 0}
                onQueued={(m) => setQueued((q2) => [...q2, m])}
              />
            </Stack>
          )}
        </Paper>
      </Box>

      <Disclose label="Where these conversations come from">
        <Stack spacing={1.5} sx={{ maxWidth: "62ch" }}>
          <Typography variant="body2" color="text.secondary">
            Contacts are Person records with a phone number — every page of them, followed until the
            kernel says there are no more — among those tagged customer, employee or vendor that your
            own tags also let you see. Your own record is pinned to the top when its email matches
            the one you signed in with. Conversations are Message records, which the org's Twilio
            number's texts are polled into hourly — so a text that arrived minutes ago may not be here
            yet, and a text you send shows as queued until that poll records it.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Only the latest {model.messageCount} messages are read. A conversation missing here is not
            proof there was none.
          </Typography>
        </Stack>
      </Disclose>
    </Stack>
  );
}
